import React, { useState, useEffect, useRef } from 'react';
import { setUserTimezone, getUserTimezone, TIMEZONE_OPTIONS } from '../shared/timezone';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SettingsPage - Company profile settings
 */
export function SettingsPage({ user, onUserUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    company_name: '',
    contact_person: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    email_signature: '',
    timezone: getUserTimezone(),
  });

  // Load company profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/company-profile`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setProfile(prev => ({ ...prev, ...data.data }));
            // Cache timezone in localStorage for use across the app
            if (data.data.timezone) setUserTimezone(data.data.timezone);
          }
        }
      } catch (err) {

        setError('Laden fehlgeschlagen: ' + (err.message || 'Server nicht erreichbar'));
      } finally {
        setLoading(false);
      }
    };
    
    loadProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/api/settings/company-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify(profile)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Einstellungen gespeichert!');
        // Update cached timezone
        if (profile.timezone) setUserTimezone(profile.timezone);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('logo', file);
    
    try {
      const response = await fetch(`${API_URL}/api/settings/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        setProfile(prev => ({ ...prev, logo_url: data.url }));
        setSuccess('Logo hochgeladen!');
      }
    } catch (err) {
      setError('Logo-Upload fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">⚙️ Einstellungen</h1>
        <p className="text-gray-500 mt-1">Verwalten Sie Ihr Unternehmensprofil</p>
      </div>

      {/* Messages */}
      {success && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg">
          ✅ {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          ❌ {error}
        </div>
      )}

      {/* Company Profile Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <span>🏢</span> Unternehmensprofil
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Diese Daten werden automatisch in Agent-Kommunikation verwendet
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <div className="flex items-center gap-4">
              {profile.logo_url ? (
                <img src={profile.logo_url} alt="Logo" className="w-16 h-16 object-contain rounded-lg border" />
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                  📷
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="hidden"
                onChange={handleLogoUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Logo hochladen
              </button>
            </div>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Firmenname</label>
            <input
              type="text"
              value={profile.company_name}
              onChange={(e) => setProfile(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="z.B. Wisecall GmbH"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Contact Person */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ansprechpartner</label>
            <input
              type="text"
              value={profile.contact_person}
              onChange={(e) => setProfile(prev => ({ ...prev, contact_person: e.target.value }))}
              placeholder="z.B. Max Mustermann"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
            <textarea
              value={profile.address}
              onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Straße, PLZ Ort"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Phone & Email */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+49 123 456789"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                placeholder="info@firma.de"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            <input
              type="url"
              value={profile.website}
              onChange={(e) => setProfile(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://www.firma.de"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Email Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail-Signatur</label>
            <textarea
              value={profile.email_signature}
              onChange={(e) => setProfile(prev => ({ ...prev, email_signature: e.target.value }))}
              placeholder="Mit freundlichen Grüßen&#10;Max Mustermann"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zeitzone</label>
            <select
              value={profile.timezone || 'Europe/Berlin'}
              onChange={(e) => setProfile(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="timezone-select"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Wird für alle Zeitanzeigen in der App verwendet (Anruf-Historie, Dashboard, etc.)</p>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Speichern...' : '💾 Einstellungen speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
          <span>💡</span> Wie wird das Profil verwendet?
        </h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Der Agent verwendet diese Daten automatisch in E-Mails und SMS</li>
          <li>• Keine Platzhalter wie "[Ihr Name]" mehr in generierten Texten</li>
          <li>• Das Logo kann in E-Mail-Templates eingebunden werden</li>
        </ul>
      </div>

      {/* Google Calendar Hinweis */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h3 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
          <span>📅</span> Google Kalender
        </h3>
        <p className="text-sm text-amber-700">
          Die Google Kalender-Verbindung wird jetzt <strong>pro Agent</strong> konfiguriert. 
          Gehen Sie zu einem Agenten und aktivieren Sie dort das "Google Kalender" Tool.
        </p>
      </div>
    </div>
  );
}

export default SettingsPage;
